import { useEffect, useState } from 'react';
import imageOnboarding4 from '../../assets/image-onboarding-4.webp';
import { ProgressIndicators } from '../progress-indicators';
import { OnboardingContinueButton } from '../onboarding-continue-button';

const TARGET_AMOUNT = 1_200_200_000;
const ANIMATION_MS = 1800;

interface Step5Props {
  onContinue: () => void;
}

function formatMoneyParts(value: number): string[] {
  return `$${Math.round(value).toLocaleString('en-US')}`.split(/([,$])/);
}

export function OnboardingStep5({ onContinue }: Step5Props) {
  const [amount, setAmount] = useState(0);
  const parts = formatMoneyParts(amount);

  useEffect(() => {
    const start = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / ANIMATION_MS);
      // ease-out cubic
      const eased = 1 - (1 - progress) ** 3;
      setAmount(TARGET_AMOUNT * eased);
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
      }
    };

    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <div className="relative z-10 min-h-dvh bg-mf-neutral-900">
      <div
        data-slot="onboarding-step-5"
        className="mx-auto flex h-dvh w-full max-w-[500px] flex-col items-center overflow-hidden bg-mf-neutral-900 px-4 py-4 font-body"
      >
      <ProgressIndicators currentStep={4} variant="dark" className="shrink-0" />

      <div className="flex min-h-0 w-full flex-1 flex-col justify-center pt-8 pb-4">
        <div className="flex min-h-0 w-full flex-1 flex-col justify-center max-h-[440px]">
          <img
            src={imageOnboarding4}
            alt=""
            className="mx-auto h-full min-h-0 w-full object-contain"
          />
        </div>
      </div>

      <div className="w-full shrink-0 text-center">
        <p className="font-display text-[40px] font-extrabold leading-[48px] text-mf-green">
          {parts.map((part, index) =>
            part === ',' || part === '$' ? (
              <span key={`${part}-${index}`}>{part}</span>
            ) : (
              <span key={`${part}-${index}`} className="tabular-nums">
                {part}
              </span>
            ),
          )}
        </p>
        <p className="mt-1 font-display text-xl font-bold leading-6 text-mf-neutral-100">
          unclaimed last year
        </p>
        <p className="mt-1 text-base leading-5 text-mf-neutral-400">
          Most people never check if they&apos;re owed money — it takes two minutes to find out
        </p>
      </div>

      <OnboardingContinueButton
        onClick={onContinue}
        className="mt-4 shrink-0 bg-white text-mf-neutral-900 hover:bg-white/90"
      />
      </div>
    </div>
  );
}
